import type { AuthError, Session, User } from "@supabase/supabase-js";
import { isSupabaseConfigured } from "@/lib/env";
import {
  clearSupabaseAuthStorage,
  isLocalSupabaseUrl,
  supabase,
  supabaseSiteUrl,
} from "@/lib/supabase";
import type { BanNoticeRecord } from "@/types/auth";
import type {
  ForgotPasswordFormValues,
  LoginFormValues,
  RegisterSubmissionValues,
  ResetPasswordFormValues,
} from "@/features/auth/validation/authSchemas";

export type AuthActionErrorCode =
  | "generic"
  | "invalid-credentials"
  | "email-in-use"
  | "verification-required"
  | "current-password-invalid"
  | "account-banned"
  | "account-deleted"
  | "account-pending-deletion"
  | "missing-config";

export class AuthActionError extends Error {
  constructor(
    public readonly code: AuthActionErrorCode,
    message?: string,
    public readonly details?: {
      banNotice?: BanNoticeRecord | null;
      deletionNotice?: AccountDeletionNoticeRecord | null;
    },
  ) {
    super(message ?? code);
    this.name = "AuthActionError";
  }
}

function ensureConfigured() {
  if (!isSupabaseConfigured()) {
    throw new AuthActionError("missing-config");
  }
}

function isRetryableSessionError(error: AuthError | null) {
  if (!error) {
    return false;
  }

  const message = error.message.toLowerCase();
  return [
    "failed to fetch",
    "networkerror when attempting to fetch resource",
    "load failed",
  ].some((fragment) => message.includes(fragment));
}

async function recoverFromBrokenLocalSession() {
  await supabase.auth.stopAutoRefresh();
  clearSupabaseAuthStorage();
}

function mapSupabaseError(error: AuthError | null): AuthActionError {
  if (!error) {
    return new AuthActionError("generic");
  }

  const message = error.message.toLowerCase();

  if (message.includes("already registered") || message.includes("already exists")) {
    return new AuthActionError("email-in-use", error.message);
  }

  if (message.includes("invalid login credentials")) {
    return new AuthActionError("invalid-credentials", error.message);
  }

  if (message.includes("user is banned")) {
    return new AuthActionError("account-banned", error.message);
  }

  return new AuthActionError("generic", error.message);
}

export function authErrorToTranslationKey(code: AuthActionErrorCode) {
  switch (code) {
    case "invalid-credentials":
      return "auth.errors.invalidCredentials";
    case "email-in-use":
      return "auth.errors.emailInUse";
    case "verification-required":
      return "auth.errors.verificationRequired";
    case "current-password-invalid":
      return "auth.errors.currentPasswordInvalid";
    case "account-banned":
      return "auth.errors.accountBanned";
    case "account-deleted":
      return "auth.errors.accountDeleted";
    case "account-pending-deletion":
      return "auth.errors.accountPendingDeletion";
    case "missing-config":
      return "auth.errors.missingConfig";
    default:
      return "auth.errors.generic";
  }
}

export type AccountDeletionNoticeRecord = {
  deletion_requested_at: string | null;
  deletion_scheduled_at: string | null;
  is_deleted: boolean;
  is_pending_deletion: boolean;
};

function normalizeBanNotice(rawValue: unknown): BanNoticeRecord | null {
  if (!rawValue || typeof rawValue !== "object") {
    return null;
  }

  const candidate = rawValue as Record<string, unknown>;
  return {
    expires_at: typeof candidate.expires_at === "string" ? candidate.expires_at : null,
    is_banned: candidate.is_banned === true,
    message: typeof candidate.message === "string" ? candidate.message : null,
  };
}

function normalizeAuthUserBanNotice(user: User | null): BanNoticeRecord | null {
  const expiresAt = typeof user?.banned_until === "string" ? user.banned_until : null;
  if (!expiresAt) {
    return null;
  }

  return {
    expires_at: expiresAt,
    is_banned: true,
    message: "Your account is temporarily suspended.",
  };
}

export async function getActiveBanNotice(userOverride?: User | null): Promise<BanNoticeRecord | null> {
  ensureConfigured();

  const user = userOverride === undefined
    ? (await supabase.auth.getUser()).data.user
    : userOverride;
  const authUserBanNotice = normalizeAuthUserBanNotice(user);

  const { data, error } = await supabase.rpc("get_active_ban_notice");
  if (error) {
    if (authUserBanNotice?.is_banned) {
      return authUserBanNotice;
    }
    throw new AuthActionError("generic", error.message);
  }

  const rpcBanNotice = normalizeBanNotice(data);
  if (rpcBanNotice?.is_banned) {
    return rpcBanNotice;
  }

  return authUserBanNotice;
}

function normalizeAccountDeletionNotice(rawValue: unknown): AccountDeletionNoticeRecord | null {
  if (!rawValue || typeof rawValue !== "object") {
    return null;
  }

  const candidate = rawValue as Record<string, unknown>;
  return {
    deletion_requested_at: typeof candidate.deletion_requested_at === "string"
      ? candidate.deletion_requested_at
      : null,
    deletion_scheduled_at: typeof candidate.deletion_scheduled_at === "string"
      ? candidate.deletion_scheduled_at
      : null,
    is_deleted: candidate.is_deleted === true,
    is_pending_deletion: candidate.is_pending_deletion === true,
  };
}

export async function getAccountDeletionNotice(): Promise<AccountDeletionNoticeRecord | null> {
  ensureConfigured();

  const { data, error } = await supabase.rpc("get_account_deletion_notice");
  if (error) {
    throw new AuthActionError("generic", error.message);
  }

  return normalizeAccountDeletionNotice(data);
}

export async function getCurrentAdminAccess(): Promise<boolean> {
  ensureConfigured();

  const { data, error } = await supabase.rpc("is_admin");
  if (error) {
    throw new AuthActionError("generic", error.message);
  }

  return data === true;
}

export async function signUpWithPassword(input: RegisterSubmissionValues) {
  ensureConfigured();

  const { data, error } = await supabase.auth.signUp({
    email: input.email,
    password: input.password,
    options: {
      emailRedirectTo: `${supabaseSiteUrl}/login`,
      data: {
        first_name: input.first_name,
        last_name: input.last_name,
        phone: input.phone ?? null,
        language_pref: "en",
      },
    },
  });

  if (error) {
    throw mapSupabaseError(error);
  }

  return data;
}

export async function signInWithPassword(input: LoginFormValues) {
  ensureConfigured();

  const { data, error } = await supabase.auth.signInWithPassword({
    email: input.email,
    password: input.password,
  });

  if (error) {
    throw mapSupabaseError(error);
  }

  if (!data.user?.email_confirmed_at) {
    await supabase.auth.signOut();
    throw new AuthActionError("verification-required");
  }

  const banNotice = await getActiveBanNotice(data.user ?? null);
  if (banNotice?.is_banned) {
    await supabase.auth.signOut();
    throw new AuthActionError(
      "account-banned",
      banNotice.message ?? "This account is currently suspended.",
      { banNotice },
    );
  }

  const deletionNotice = await getAccountDeletionNotice();
  if (deletionNotice?.is_deleted) {
    await supabase.auth.signOut();
    throw new AuthActionError("account-deleted", "This account has been deleted.", {
      deletionNotice,
    });
  }

  if (deletionNotice?.is_pending_deletion) {
    await supabase.auth.signOut();
    throw new AuthActionError("account-pending-deletion", "This account is pending deletion.", {
      deletionNotice,
    });
  }

  return data.session;
}

export async function bootstrapSession(): Promise<Session | null> {
  if (!isSupabaseConfigured()) {
    return null;
  }

  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (error) {
    if (isLocalSupabaseUrl && isRetryableSessionError(error)) {
      await recoverFromBrokenLocalSession();
    }

    return null;
  }

  return session;
}

export function onAuthStateChange(
  callback: (session: Session | null) => Promise<void> | void,
) {
  return supabase.auth.onAuthStateChange((_event, session) => {
    void callback(session);
  });
}

export async function signOut() {
  if (!isSupabaseConfigured()) {
    return;
  }

  await supabase.auth.signOut();
}

export async function sendPasswordResetEmail(input: ForgotPasswordFormValues) {
  ensureConfigured();

  const { error } = await supabase.auth.resetPasswordForEmail(input.email, {
    redirectTo: `${supabaseSiteUrl}/reset-password`,
  });

  if (error) {
    throw mapSupabaseError(error);
  }
}

export async function updatePassword(input: ResetPasswordFormValues) {
  ensureConfigured();

  const { error } = await supabase.auth.updateUser({
    password: input.password,
  });

  if (error) {
    throw mapSupabaseError(error);
  }
}

export async function confirmCurrentPasswordAndUpdatePassword(input: {
  email: string;
  currentPassword: string;
  newPassword: string;
}) {
  ensureConfigured();

  const reauth = await supabase.auth.signInWithPassword({
    email: input.email,
    password: input.currentPassword,
  });

  if (reauth.error) {
    throw new AuthActionError("current-password-invalid", reauth.error.message);
  }

  const { error } = await supabase.auth.updateUser({
    password: input.newPassword,
  });

  if (error) {
    throw mapSupabaseError(error);
  }
}

export async function recoverPendingDeletionAccount(input: {
  email: string;
  password: string;
}) {
  ensureConfigured();

  const { error } = await supabase.functions.invoke("account-deletion", {
    body: {
      action: "recover",
      email: input.email,
      password: input.password,
    },
  });

  if (error) {
    throw new AuthActionError("generic", error.message);
  }
}
